import { useEffect, useEffectEvent, useRef, useState, useSyncExternalStore, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AudioLines, Check, Clock3, FileUp, Loader2, Mic, SendHorizontal, Square, Volume2, X } from 'lucide-react'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { invalidateQuoteOverview } from '../api/queries'
import type { Quote } from '../api/types'
import { pastedFiles, quoteFilesKey, type QuoteFile } from '../files/api'
import { FileLink, QuoteFiles } from '../files/quote-files'
import { useQuoteFiles } from '../files/use-quote-files'
import { AiProgress } from '../ui/ai-motion'
import { aiEnter, useArrivals } from '../ui/ai-motion-state'
import { AssistantArtifactLinks } from './artifacts'
import {
  assistantActionDisplay,
  assistantText,
  createAssistantConversation,
  importCards,
  type ActionState,
  type AssistantConversation,
  type ImportOf,
} from './conversation'
import { refreshAssistantQuote, workerLabel, workHistory, workLabel } from './chat-helpers'
import { captionRows, useLiveVoice } from './live-voice'
import { VoiceMessage } from './voice-message'
import { useVoiceRecorder } from './voice-recorder'
import { ComposeTextarea } from '@/components/compose-field'

const ACTION_STATUS: Record<ActionState['status'], string> = {
  pending: 'Review',
  applying: 'Applying',
  applied: '',
  rejected: 'Rejected',
  failed: 'Failed',
  stale: 'Review again',
}
const NEEDS_DECISION: ActionState['status'][] = ['pending', 'failed', 'stale']
const LIVE_STATUS = {
  connecting: 'Connecting...',
  reconnecting: 'Reconnecting...',
  listening: 'Listening',
  speaking: 'SmartQuote speaking',
  closing: 'Ending voice...',
  idle: '',
  error: '',
} as const

function useConversation(scope: string, actorId: number, quote: Quote | null): AssistantConversation {
  const client = useQueryClient()
  const api = useQuoteBuilderApi()
  const [conversation] = useState(() => {
    const key = ['ai-conversation', scope, actorId]
    const existing = client.getQueryData<AssistantConversation>(key)
    if (existing) return existing
    let cachedQuoteId = quote?.id
    const created: AssistantConversation = createAssistantConversation(
      api,
      quote,
      (next) => {
        // The conversation follows a quote it created: that quote's scope now resolves to it.
        if (cachedQuoteId && cachedQuoteId !== next.id) {
          client.removeQueries({ queryKey: ['ai-conversation', cachedQuoteId, actorId], exact: true })
        }
        cachedQuoteId = next.id
        const quoteKey = ['ai-conversation', next.id, actorId]
        client.setQueryDefaults(quoteKey, { gcTime: Infinity })
        client.setQueryData(quoteKey, created)
        refreshAssistantQuote(client, next)
      },
      (customer) => {
        void client.invalidateQueries({ queryKey: ['customer', customer.id] })
        void invalidateQuoteOverview(client)
        for (const name of ['customers', 'customer-search']) void client.invalidateQueries({ queryKey: [name] })
      },
    )
    client.setQueryDefaults(key, { gcTime: Infinity })
    client.setQueryData(key, created)
    return created
  })
  return conversation
}

function FieldList({ fields }: { fields: Array<{ label: string; before: string | null; after: string }> }) {
  return (
    <dl className="space-y-1.5">
      {fields.map((field, index) => (
        <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 text-xs" key={`${field.label}:${index}`}>
          <dt className="text-muted-foreground">{field.label}</dt>
          <dd className="min-w-0 break-words">
            {field.before !== null ? (
              <span className="mb-0.5 block text-muted-foreground line-through">{field.before}</span>
            ) : null}
            <span className="font-medium tabular-nums">{field.after}</span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

/** An equipment import the chat started or finished; it opens the import's review (USA-1340, USA-1342). */
function ImportCard({ className, item, quoteId }: { className?: string; item: ImportOf; quoteId?: string }) {
  return (
    <div className={cn('flex', className)}>
      <Link
        className="flex max-w-[88%] items-center gap-2.5 rounded-lg border bg-card px-3 py-2 text-sm outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        params={{ quoteId: quoteId ?? '', stage: 'equipment' }}
        search={{ import: 1, documentImport: item.id }}
        to="/quotes/$quoteId/workflow/$stage"
      >
        <FileUp aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate font-medium">{item.fileName}</span>
          <span className="block text-xs text-muted-foreground">Equipment import · Open review</span>
        </span>
      </Link>
    </div>
  )
}

function ActionCard({
  action,
  quote,
  decisionsOpen,
  onApprove,
  onReject,
}: {
  action: ActionState
  quote: Quote | null
  decisionsOpen: boolean
  onApprove: () => void
  onReject: () => void
}) {
  const display = assistantActionDisplay(action, quote)
  const status = ACTION_STATUS[action.status]
  return (
    <article aria-label={display.title} className="space-y-3 p-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex min-w-0 items-start gap-1.5 break-words text-sm font-medium">
          {action.status === 'applied' ? <Check aria-label="Applied" className="mt-0.5 size-3.5 shrink-0 text-emerald-600" /> : null}
          {display.title}
        </h3>
        {status ? (
          <span
            className={cn(
              'shrink-0 text-xs font-medium',
              ['failed', 'stale'].includes(action.status) ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {status}
          </span>
        ) : null}
      </div>
      {display.products.length ? (
        <div className="divide-y">
          {display.products.map((product) => (
            <div className="space-y-2 py-2 first:pt-0 last:pb-0" key={product.id}>
              <POLineItemProductCell
                model={product.sku || product.title}
                description={product.sku ? product.title : null}
                imageUrl={product.image_url}
                productUuid={product.uuid}
                manufacturerName={product.manufacturer}
                manufacturerUuid={product.manufacturer_uuid}
                collapseLongDescription
                imageClassName="size-14"
              />
              <FieldList fields={product.fields} />
            </div>
          ))}
        </div>
      ) : null}
      {display.fields.length ? (
        <FieldList fields={display.fields} />
      ) : !display.products.length && display.detail && display.detail !== display.title ? (
        <p className="whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
          {NEEDS_DECISION.includes(action.status) ? display.detail : display.detail.slice(0, 180)}
        </p>
      ) : null}
      {action.error ? (
        <p className="text-xs text-destructive" role="alert">
          {assistantText(action.error) || 'Could not apply this change. Ask for an updated proposal.'}
        </p>
      ) : null}
      {decisionsOpen && ['pending', 'failed', 'applying'].includes(action.status) ? (
        <div className="flex justify-end gap-2">
          <Button disabled={action.status === 'applying'} onClick={onReject} size="sm" variant="outline">
            Reject
          </Button>
          <LoadingButton
            className="bg-violet-600 text-white hover:bg-violet-700"
            loading={action.status === 'applying'}
            loadingText="Applying..."
            onClick={onApprove}
            size="sm"
          >
            {action.status === 'failed' ? 'Retry' : action.approval === 'confirmation' ? 'Confirm' : 'Approve'}
          </LoadingButton>
        </div>
      ) : null}
    </article>
  )
}

export function AssistantChat({
  scope,
  actorId,
  quote = null,
  onQuoteChange,
  visible = true,
}: {
  scope: string
  actorId: number
  quote?: Quote | null
  onQuoteChange?: (quote: Quote, navigate?: boolean) => void
  /** Minimizing the panel ends the microphone: live voice and any recording stop. */
  visible?: boolean
}) {
  const callback = useRef(onQuoteChange)
  useEffect(() => {
    callback.current = onQuoteChange
  })
  const api = useQuoteBuilderApi()
  const conversation = useConversation(scope, actorId, quote)
  const state = useSyncExternalStore(conversation.subscribe, conversation.getSnapshot, conversation.getSnapshot)
  const voice = useVoiceRecorder((clip) => void conversation.submit(clip.transcript, 'voice', [], { clip }))
  const live = useLiveVoice(api, conversation)
  const releaseMicrophone = useEffectEvent(() => {
    voice.cancel()
    live.stop()
  })
  useEffect(() => {
    if (!visible) releaseMicrophone()
  }, [visible])
  const captions = useRef<HTMLDivElement>(null)
  const followCaptions = useRef(true)
  useEffect(() => {
    if (followCaptions.current && captions.current) captions.current.scrollTop = captions.current.scrollHeight
  }, [state.transcripts])
  const backgroundRunning = state.messages.some((message) => message.running)
  useEffect(() => {
    if (!backgroundRunning) return
    const timer = setInterval(() => void conversation.refreshRunning(), 2000)
    return () => clearInterval(timer)
  }, [conversation, backgroundRunning])
  const active =
    [...state.messages].reverse().find((message) => message.running) ||
    (state.pending > 0 ? [...state.messages].reverse().find((message) => message.role === 'assistant') : undefined)
  const working = state.pending > 0 || !!active
  const activityLabel = workLabel(state.pending > 0 ? state.progress : active?.progress) || 'Working on quote'
  const arrivedMessages = useArrivals(state.messages.map((message) => message.id))
  const cards = importCards(state.messages)
  const arrivedWorkers = useArrivals(state.messages.flatMap((message) => message.workers?.map((worker) => worker.id) ?? []))
  // Ready files go with the next message once (demo `assistantReadyFiles`); before a quote exists
  // they are the user's unassigned files and bind to the quote the assistant creates.
  const fileQuoteId = state.quote?.id || quote?.id || null
  const library = useQuoteFiles(fileQuoteId)
  const readyFiles = (library.files.data ?? []).filter((file) => file.ready && file.quote_id === fileQuoteId)
  const sentFileIds = new Set(state.messages.flatMap((message) => message.fileIds ?? []))
  const requestFileIds = readyFiles.filter((file) => !fileQuoteId || !sentFileIds.has(file.id)).map((file) => file.id)
  const filesById = new Map((library.files.data ?? []).map((file): [string, QuoteFile] => [file.id, file]))
  const uploadDisabled = !fileQuoteId && state.pending > 0
  const client = useQueryClient()
  useEffect(() => {
    // Files attached before the quote existed move to it when the assistant creates it.
    void client.invalidateQueries({ queryKey: quoteFilesKey(fileQuoteId), exact: true })
    if (fileQuoteId) void client.invalidateQueries({ queryKey: quoteFilesKey(null), exact: true })
  }, [client, fileQuoteId, state.pending])
  const end = useRef<HTMLDivElement>(null)
  const log = useRef<HTMLDivElement>(null)
  const follow = useRef(true)
  const resizes = useRef<ResizeObserver | null>(null)
  useEffect(() => {
    let previous = conversation.getSnapshot().quote
    if (previous && (!quote || previous.revision > quote.revision)) callback.current?.(previous, false)
    return conversation.subscribe(() => {
      const next = conversation.getSnapshot().quote
      if (next && next !== previous) {
        const created = !previous || next.id !== previous.id
        previous = next
        callback.current?.(next, created)
      }
    })
    // Subscribe once per conversation; the latest quote prop is synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation])
  useEffect(() => {
    if (quote) conversation.syncQuote(quote)
  }, [conversation, quote])
  useEffect(() => {
    if (!scope.startsWith('global')) void conversation.restore()
  }, [conversation, scope])
  useEffect(() => {
    // An equipment import finished by SmartQuote while the panel was closed joins the conversation.
    if (visible && !scope.startsWith('global')) void conversation.addImports()
  }, [conversation, scope, visible])
  useEffect(() => {
    if (follow.current) end.current?.scrollIntoView({ block: 'nearest' })
  }, [state.messages, state.pending])
  useEffect(() => {
    const container = log.current
    if (!container) return
    const observer = new ResizeObserver(() => {
      if (follow.current && container.clientHeight) container.scrollTop = container.scrollHeight
    })
    observer.observe(container)
    resizes.current = observer
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    // Messages keep growing after they render (photos, follow-up questions): keep following them.
    for (const child of log.current?.children ?? []) resizes.current?.observe(child)
  }, [state.messages])
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!state.draft.trim() && !requestFileIds.length) return
    const draft = state.draft
    conversation.setDraft('')
    void conversation.submit(draft, 'chat', requestFileIds)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {state.quote ? (
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2 text-xs text-muted-foreground">
          <span className="truncate">
            <span className="font-mono">{state.quote.quote_number}</span> · {state.quote.title}
          </span>
          <Link
            className="shrink-0 font-medium text-foreground underline-offset-4 hover:underline"
            params={{ quoteId: state.quote.id, stage: 'equipment' }}
            to="/quotes/$quoteId/workflow/$stage"
          >
            Open quote
          </Link>
        </div>
      ) : null}
      <div
        aria-label="Conversation"
        aria-live="polite"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-4"
        onScroll={(event) => {
          const node = event.currentTarget
          if (node.clientHeight) follow.current = node.scrollHeight - node.scrollTop - node.clientHeight < 32
        }}
        ref={log}
        role="log"
      >
        {!state.messages.length ? (
          <p className="py-2 text-sm text-muted-foreground">{quote ? 'What should change?' : 'What quote can I help you build?'}</p>
        ) : null}
        {state.messages.map((message) => {
          const history = workHistory(message.steps, message.activity)
          const decisions = message.actions?.filter((action) => NEEDS_DECISION.includes(action.status)).length ?? 0
          const decisionsOpen =
            !message.running && !message.request?.reject_action_id && !(state.pending > 0 && state.autoApprove)
          return (
            <div className={cn('space-y-2', arrivedMessages.has(message.id) && aiEnter)} key={message.id}>
              {message.role === 'user' && message.importOf ? (
                cards.get(message.id)?.map((item) => (
                  <ImportCard className="justify-end" item={item} key={item.id} quoteId={state.quote?.id} />
                ))
              ) : message.role === 'user' && (assistantText(message.content) || message.clip) ? (
                <div className="flex justify-end">
                  {message.clip ? (
                    <VoiceMessage clip={{ ...message.clip, transcript: assistantText(message.clip.transcript) }} outgoing />
                  ) : (
                    <p className="max-w-[88%] whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-2 text-sm leading-5 text-primary-foreground">
                      {assistantText(message.content)}
                    </p>
                  )}
                </div>
              ) : null}
              {message.role === 'user' && message.fileIds?.length ? (
                <div aria-label="Message attachments" className="ml-auto flex max-w-[88%] flex-wrap justify-end gap-1">
                  {message.fileIds.map((id) => {
                    const file = filesById.get(id)
                    return file ? (
                      <FileLink file={file} key={id} small />
                    ) : (
                      <span className="rounded-md border px-2 py-1 text-xs text-muted-foreground" key={id}>
                        {library.files.isPending ? 'Loading attachment…' : 'File removed'}
                      </span>
                    )
                  })}
                </div>
              ) : null}
              {message.role === 'user' && message.status === 'queued' ? (
                <p className="text-right text-xs text-muted-foreground">Queued</p>
              ) : null}
              {message.role === 'assistant' && message.workers?.length ? (
                <div aria-label="Parallel work" className="max-w-[92%] rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-2 dark:border-violet-900 dark:bg-violet-950/30">
                  <p className="mb-1 text-xs font-medium text-violet-700 dark:text-violet-300">Parallel work</p>
                  <ul className="space-y-1">
                    {message.workers.map((worker) => {
                      // A saved worker of a stopped turn is paused, not working.
                      const status =
                        worker.status === 'running' && !(message.running || message === active) ? 'cancelled' : worker.status
                      return (
                      <li
                        className={cn('relative flex items-start gap-2 overflow-hidden rounded text-xs', arrivedWorkers.has(worker.id) && aiEnter)}
                        key={worker.id}
                      >
                        {status === 'running' ? (
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 left-0 w-1/3 animate-[qb-ai-shimmer_1.6s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent"
                          />
                        ) : null}
                        <span className="mt-0.5 shrink-0">
                          {status === 'running' ? (
                            <Loader2 aria-label="Working" className="size-3 animate-spin text-violet-600 motion-reduce:animate-none" />
                          ) : status === 'ready' ? (
                            <Clock3 aria-label="Ready for review" className="size-3 text-violet-600" />
                          ) : status === 'complete' ? (
                            <Check aria-label="Complete" className="size-3 text-emerald-600" />
                          ) : (
                            <Clock3 aria-label={status === 'cancelled' ? 'Stopped' : 'Needs attention'} className="size-3 text-muted-foreground" />
                          )}
                        </span>
                        <span className="min-w-0 break-words">{workerLabel(worker)}</span>
                      </li>
                      )
                    })}
                  </ul>
                </div>
              ) : null}
              {message.role === 'assistant' && message.actions?.length ? (
                <details className="group max-w-[92%] rounded-lg border bg-card" open={decisions > 0 || undefined}>
                  <summary className="cursor-pointer list-none rounded-lg px-3 py-2 text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
                    {message.actions.length} {message.actions.length === 1 ? 'change' : 'changes'}
                    {decisions ? <span className="text-violet-700 dark:text-violet-300"> · {decisions} to review</span> : null}
                  </summary>
                  <div className="divide-y border-t">
                    {message.actions.map((action) => (
                      <ActionCard
                        action={action}
                        decisionsOpen={decisionsOpen && (state.pending === 0 || action.status === 'applying')}
                        key={action.token}
                        onApprove={() =>
                          void conversation.apply(action.token, action.approval, action.approval === 'confirmation' ? action.title : null)
                        }
                        onReject={() => void conversation.reject(action.token)}
                        quote={state.quote}
                      />
                    ))}
                  </div>
                </details>
              ) : null}
              {message.role === 'assistant' && !message.running && history.length ? (
                <details className="max-w-[92%] rounded-lg border text-xs text-muted-foreground">
                  <summary className="cursor-pointer list-none rounded-lg px-3 py-2 font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
                    Work history
                  </summary>
                  <ol className="max-h-36 list-decimal space-y-1 overflow-y-auto overscroll-contain border-t py-2 pl-7 pr-3 leading-5">
                    {history.map((label, index) => (
                      <li className="break-words" key={`${label}:${index}`}>
                        {label}
                      </li>
                    ))}
                  </ol>
                </details>
              ) : null}
              {message.role === 'assistant' &&
              assistantText(message.content) &&
              !(conversation.isContinued(message.id) && message.content === 'Stopped before finishing. Applied changes are saved.') ? (
                <p className="max-w-[92%] whitespace-pre-wrap break-words rounded-lg bg-muted px-3 py-2 text-sm leading-5">
                  {assistantText(message.content)}
                </p>
              ) : null}
              {message.role === 'assistant'
                ? message.questions
                    ?.filter((question) => assistantText(question))
                    .map((question) => (
                      <p className="text-sm leading-5 text-muted-foreground" key={question}>
                        {assistantText(question)}
                      </p>
                    ))
                : null}
              {message.role === 'assistant' && !message.importOf
                ? cards.get(message.id)?.map((item) => <ImportCard item={item} key={item.id} quoteId={state.quote?.id} />)
                : null}
              {message.role === 'assistant' ? <AssistantArtifactLinks artifacts={message.artifacts} /> : null}
              {message.role === 'assistant' && message.error && !conversation.isContinued(message.id) ? (
                <p className="text-sm text-destructive" role="alert">
                  {message.error}
                </p>
              ) : null}
              {message.role === 'assistant' && !message.running && message.turnStatus === 'interrupted' && conversation.isContinued(message.id) ? (
                <p className="text-xs text-muted-foreground">Continued in another message</p>
              ) : null}
              {message.role === 'assistant' &&
              !message.running &&
              !conversation.isContinued(message.id) &&
              message.request?.turn_id &&
              message.request.source !== 'import' &&
              (message.turnStatus === 'interrupted' || (message.turnStatus === 'waiting' && !decisions)) ? (
                <Button disabled={state.pending > 0} onClick={() => void conversation.resume(message.id)} size="sm" variant="outline">
                  {message.error ? 'Retry' : 'Continue'}
                </Button>
              ) : null}
            </div>
          )
        })}
        <div ref={end} />
      </div>
      {state.transcripts.length ? (
        <details className="shrink-0 border-t px-4 py-1.5" open={live.active}>
          <summary className="cursor-pointer rounded py-1 text-xs font-medium text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
            Live transcript
          </summary>
          <div
            aria-label="Live transcript"
            aria-live="polite"
            className="max-h-28 space-y-2 overflow-y-auto overscroll-contain pt-1"
            onScroll={(event) => {
              const node = event.currentTarget
              if (node.clientHeight) followCaptions.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24
            }}
            ref={captions}
            role="log"
          >
            {captionRows(state.transcripts).map((row) => (
              <p
                className={cn('whitespace-pre-wrap break-words text-sm leading-5', row.role === 'user' ? 'text-foreground' : 'text-muted-foreground')}
                key={row.id}
              >
                <span className="mr-2 text-xs font-medium text-violet-700 dark:text-violet-300">{row.role === 'user' ? 'You' : 'SmartQuote'}</span>
                {row.text}
              </p>
            ))}
          </div>
        </details>
      ) : null}
      {working ? (
        <div className="relative shrink-0 border-t bg-background px-4 py-2 text-xs text-muted-foreground">
          <AiProgress className="absolute inset-x-0 -top-px" from={0.06} seconds={90} to={0.92} />
          <div className="flex min-w-0 items-center gap-2" role="status">
            <Loader2 aria-hidden="true" className="size-3.5 shrink-0 animate-spin text-violet-600 motion-reduce:animate-none" />
            <span className={cn('truncate', aiEnter)} key={activityLabel}>
              {activityLabel}
              {state.pending > 1 ? ` · ${state.pending - 1} queued` : ''}
            </span>
          </div>
        </div>
      ) : null}
      <form
        className="border-t p-3"
        onPaste={(event) => {
          if (uploadDisabled || voice.status !== 'idle') return
          const files = pastedFiles(event)
          if (files.length) void library.add(files)
        }}
        onSubmit={submit}
      >
        <QuoteFiles compact disabled={uploadDisabled || voice.status !== 'idle'} quoteId={fileQuoteId} />
        {live.active ? (
          <div
            className="mb-2 flex min-h-9 items-center gap-2 rounded-lg bg-violet-50 px-3 py-2 text-sm text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
            role="status"
          >
            <AudioLines aria-hidden="true" className="size-4" />
            <span className="flex-1 font-medium">{LIVE_STATUS[live.status]}</span>
            <span className="text-xs text-muted-foreground">Live voice</span>
          </div>
        ) : null}
        {live.playbackBlocked ? (
          <Button className="mb-2" onClick={() => void live.play()} size="sm" type="button" variant="outline">
            <Volume2 />
            Enable speaker
          </Button>
        ) : null}
        {live.error ? (
          <p className="mb-2 text-sm text-destructive" role="alert">
            {live.error}
          </p>
        ) : null}
        {voice.status !== 'idle' ? (
          <div className="mb-2 flex min-h-9 items-center justify-between gap-2 text-sm text-muted-foreground">
            <div className="flex min-w-0 items-center gap-2">
              <span role="status">
                {voice.status === 'recording' ? 'Recording' : voice.status === 'transcribing' ? 'Transcribing...' : 'Opening microphone...'}
              </span>
              {voice.status === 'recording' ? (
                <span className="tabular-nums">
                  {Math.floor(voice.seconds / 60)}:{String(voice.seconds % 60).padStart(2, '0')}
                </span>
              ) : null}
            </div>
            <Button onClick={voice.cancel} size="sm" type="button" variant="outline">
              <X />
              Cancel
            </Button>
          </div>
        ) : null}
        {voice.error ? (
          <p className="mb-2 text-sm text-destructive" role="alert">
            {voice.error}
          </p>
        ) : null}
        <div className="flex items-end gap-1 rounded-lg border bg-background p-1 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
          <label className="min-w-0 flex-1 pl-2">
            <span className="sr-only">Message SmartQuote</span>
            <ComposeTextarea bare
              className="block max-h-32 min-h-9 w-full resize-none bg-transparent px-1 py-2 text-sm leading-5 outline-none placeholder:text-muted-foreground"
              onChange={(event) => conversation.setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault()
                  event.currentTarget.form?.requestSubmit()
                }
              }}
              placeholder="Ask SmartQuote"
              rows={1}
              value={state.draft}
            />
          </label>
          <Button
            aria-label={live.active ? 'End live voice' : live.status === 'error' ? 'Reconnect live voice' : 'Start live voice'}
            aria-pressed={live.active}
            className={cn(live.active && 'bg-violet-600 text-white hover:bg-violet-700 hover:text-white')}
            disabled={(!live.active && voice.status !== 'idle') || live.status === 'closing'}
            onClick={() => (live.active ? live.stop() : void live.start())}
            size="icon"
            title={live.active ? 'End live voice' : 'Live voice'}
            type="button"
            variant="ghost"
          >
            {live.status === 'connecting' || live.status === 'closing' ? (
              <Loader2 className="animate-spin motion-reduce:animate-none" />
            ) : live.active ? (
              <Square className="fill-current" />
            ) : (
              <Mic />
            )}
          </Button>
          <Button
            aria-label="Send to SmartQuote"
            className="bg-violet-600 text-white hover:bg-violet-700"
            disabled={!state.draft.trim() && !requestFileIds.length}
            size="icon"
            type="submit"
          >
            <SendHorizontal />
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <label className="flex min-h-8 items-center gap-2 text-xs font-medium text-muted-foreground">
            <Switch
              checked={state.autoApprove}
              disabled={state.pending > 0}
              onCheckedChange={(checked) => conversation.setAutoApprove(checked)}
            />
            Auto-approve edits
          </label>
          <Button
            className="h-8 px-1 text-xs text-muted-foreground"
            disabled={live.active || voice.status === 'starting' || voice.status === 'transcribing'}
            onClick={() => (voice.status === 'recording' ? voice.stop() : void voice.start())}
            type="button"
            variant="ghost"
          >
            <AudioLines />
            {voice.status === 'recording' ? 'Stop recording and send' : 'Record voice message'}
          </Button>
        </div>
      </form>
    </div>
  )
}

